
import {Component} from 'vue-property-decorator';
import uuid from 'uuid';
import * as echarts from 'echarts';
import SecondaryLineBarChartFilterable from '@/components/reports-v3/components/filterables/SecondaryLineBarChartFilterable.vue';
import moment from 'moment';
import SelectFilter from '@/components/reports-v3/components/filters/SelectFilter.vue';
import {COL, sqlExtractYear, sqlExtractMonth, sqlSumByDocType, sqlSum, buildNewCustomerFilter, getTableName} from '@/worker/db/sqlHelpers';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {PermissionsGroup, ValueText} from '@/store/models.def';
import {GobiColor} from '@/helpers/color';
import gdbx from '@/store/modules/gdbx';
import {DataViewFormatter} from '@/components/reports-v3/components/elements/charts/helpers/dataViewFormatter';
import DataInfo from '@/components/reports-v3/components/elements/DataInfo.vue';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
    components: {
        SecondaryLineBarChartFilterable,
        SelectFilter,
        DataInfo,
        IncludeCreditNoteToggle,
        IncludeDebitNoteToggle,
        SlPhModuleOnlyToggle,
    },
})
export default class AgentSalesTrendLineChart extends FilterWidget {
    public get permissionIds(): PermissionsGroup[] {
        return ['sales', 'customers'];
    }

    public filterIds: Array<
        | 'date'
        | 'dateAsOf'
        | 'dateRange'
        | 'stockItems'
        | 'agents'
        | 'customers'
        | 'suppliers'
        | 'docproject'
        | 'dtlproject'
    > = [];

    public xAxisData: string[] = [];
    public series: echarts.ECharts[] | echarts.ECharts | any = [];

    public selectedColumn: string = this.columnList[0].value;
    public selectedComparedYear: number = this.compareYearList[0];
    public selectedTimeframe: string = this.timeframe[0];
    public yearEndTotal: string = '';
    public comparedYearEndTotal: string = '';

    public columnDatas: number[] = [];
    public comparedColumnDatas: number[] = [];

    // Cached raw data - avoid refetching when only column/timeframe changes
    private _cachedData: {
        // SL data this year
        slIvCsAmounts: number[];
        slDnAmounts: number[];
        slCnAmounts: number[];
        slIvCsProfits: number[];
        slDnProfits: number[];
        slCnProfits: number[];
        // AR data this year
        arIvAmounts: number[];
        arCnAmounts: number[];
        arDnAmounts: number[];
        // SL data last year
        slIvCsAmountsLY: number[];
        slDnAmountsLY: number[];
        slCnAmountsLY: number[];
        slIvCsProfitsLY: number[];
        slDnProfitsLY: number[];
        slCnProfitsLY: number[];
        // AR data last year
        arIvAmountsLY: number[];
        arCnAmountsLY: number[];
        arDnAmountsLY: number[];
        // New customer data this year
        slNewIvCsAmounts: number[];
        slNewDnAmounts: number[];
        slNewCnAmounts: number[];
        slNewIvCsProfits: number[];
        slNewDnProfits: number[];
        slNewCnProfits: number[];
        arNewIvAmounts: number[];
        arNewCnAmounts: number[];
        arNewDnAmounts: number[];
        // New customer data last year
        slNewIvCsAmountsLY: number[];
        slNewDnAmountsLY: number[];
        slNewCnAmountsLY: number[];
        slNewIvCsProfitsLY: number[];
        slNewDnProfitsLY: number[];
        slNewCnProfitsLY: number[];
        arNewIvAmountsLY: number[];
        arNewCnAmountsLY: number[];
        arNewDnAmountsLY: number[];
        // New customer counts (for newCustomer column)
        newCustomerCounts: number[];
        comparedNewCustomerCounts: number[];
    } | null = null;
    private _cachedDataKey: string = '';

    /**
     * Key for data fetching - only changes when filters change (not column/timeframe)
     */
    private get _dataFetchKey(): string {
        return JSON.stringify([
            this.computedYearRange,
            this.compareYearRange,
            this.selectedAgents,
            this.selectedCustomers,
        ]);
    }

    /**
     * Applies 3-month or 6-month grouping to columnDatas and comparedColumnDatas.
     * Assumes input arrays are 12 monthly values in oldest-first order (index 0 = oldest month).
     * Output will also be in oldest-first order.
     * @param monthLabels - Array of 12 month labels (oldest to newest)
     */
    public applyTimeframeGrouping(monthLabels: Array<{ text: string }>) {
        if (this.selectedTimeframe === '3 months') {
            // Group 12 months into 4 quarters (oldest first)
            // Input: [Feb, Mar, Apr, May, Jun, Jul, Aug, Sep, Oct, Nov, Dec, Jan] (oldest first)
            // Output: [Feb-Apr, May-Jul, Aug-Oct, Nov-Jan] (oldest first)
            const groupedData: number[] = [];
            const groupedComparedData: number[] = [];

            for (let i = 0; i < 4; i++) {
                const startIdx = i * 3;
                groupedData.push(
                    this.columnDatas[startIdx] + this.columnDatas[startIdx + 1] + this.columnDatas[startIdx + 2],
                );
                groupedComparedData.push(
                    this.comparedColumnDatas[startIdx]
                    + this.comparedColumnDatas[startIdx + 1]
                    + this.comparedColumnDatas[startIdx + 2],
                );
            }

            this.columnDatas = groupedData;
            this.comparedColumnDatas = groupedComparedData;

            // Labels for oldest-first order: [0-2], [3-5], [6-8], [9-11]
            this.xAxisData = [
                `${monthLabels[0].text} - ${monthLabels[2].text}`,
                `${monthLabels[3].text} - ${monthLabels[5].text}`,
                `${monthLabels[6].text} - ${monthLabels[8].text}`,
                `${monthLabels[9].text} - ${monthLabels[11].text}`,
            ];
        } else if (this.selectedTimeframe === '6 months') {
            // Group 12 months into 2 halves (oldest first)
            const groupedData: number[] = [];
            const groupedComparedData: number[] = [];

            // First half: oldest 6 months (indices 0-5)
            groupedData.push(
                this.columnDatas[0] + this.columnDatas[1] + this.columnDatas[2] +
                this.columnDatas[3] + this.columnDatas[4] + this.columnDatas[5],
            );
            groupedComparedData.push(
                this.comparedColumnDatas[0] + this.comparedColumnDatas[1] + this.comparedColumnDatas[2] +
                this.comparedColumnDatas[3] + this.comparedColumnDatas[4] + this.comparedColumnDatas[5],
            );

            // Second half: newest 6 months (indices 6-11)
            groupedData.push(
                this.columnDatas[6] + this.columnDatas[7] + this.columnDatas[8] +
                this.columnDatas[9] + this.columnDatas[10] + this.columnDatas[11],
            );
            groupedComparedData.push(
                this.comparedColumnDatas[6] + this.comparedColumnDatas[7] + this.comparedColumnDatas[8] +
                this.comparedColumnDatas[9] + this.comparedColumnDatas[10] + this.comparedColumnDatas[11],
            );

            this.columnDatas = groupedData;
            this.comparedColumnDatas = groupedComparedData;

            // Labels for oldest-first order: [0-5], [6-11]
            this.xAxisData = [
                `${monthLabels[0].text} - ${monthLabels[5].text}`,
                `${monthLabels[6].text} - ${monthLabels[11].text}`,
            ];
        }
    }

    public get yAxisLabel2() {
        // Line chart feature is disabled, no secondary axis needed
        return '';
    }

    public get compareYearList() {
        const currentYear = moment(this.selectedAsOfDate).year();

        const list: number[] = [];

        for (let i = 1; i < 30; i++) {
            const year = currentYear - i;
            list.push(year);
        }

        return list;
    }

    public get timeframe() {
        return [
            'Monthly',
            '3 months',
            '6 months',
        ];
    }

    public get columnList(): ValueText[] {
        const list = [
            {text: 'Sales Amt(All Customers)', value: 'allSalesAmount'},
            {text: 'Sales Amt(New Customers)', value: 'newSalesAmount'},
            {text: 'Sales Amt(Existing Customers)', value: 'existingSalesAmount'},
            {text: 'Sales Profit(All Customers)', value: 'allProfitAmount'},
            {text: 'Sales Profit(New Customers)', value: 'newProfitAmount'},
            {text: 'Sales Profit(Existing Customers)', value: 'existingProfitAmount'},
            {text: 'Credit Note Amt(All Customers)', value: 'allCreditNote'},
            {text: 'Number of New Customers', value: 'newCustomer'},
        ];

        return list;
    }

    public get currentYear() {
        return moment(this.selectedAsOfDate).year();
    }

    public get currentMonth() {
        return moment(this.selectedAsOfDate).month();
    }

    public get computedYearRange(): [number, number] {
        return [
            moment(this.selectedDateRange[1])
                .add(-1, 'year')
                .add(1, 'month')
                .startOf('month').valueOf(),
            moment(this.selectedDateRange[1]).valueOf(),
        ];
    }

    public get compareYearRange(): [number, number] {
        return [
            moment(this.computedYearRange[0])
                .add(-1, 'year')
                .valueOf(),
            moment(this.computedYearRange[1])
                .add(-1, 'year')
                .endOf('month')
                .valueOf(),
        ];
    }

    public get yAxisLabel() {
        let label = '';

        if (this.selectedColumn !== 'newCustomer') {
            label = gdbx.currencySymbol;
        }

        return label;
    }

    public get decimal() {
        return gdbx.numDecimal;
    }

    public get columnName() {
        const found = this.columnList.find(
            (vt) => vt.value === this.selectedColumn,
        );
        let result = '';
        if (found) {
            result = found.text;
        }
        return result;
    }

    public get dataViewFormatter() {
        return DataViewFormatter.salesTracker;
    }

    public get includeCreditNote() {
        return settingx.now.includeCreditNote;
    }

    public get includeDebitNote() {
        return settingx.now.includeDebitNote;
    }

    public get slPhModuleOnly() {
        return settingx.now.slPhModuleOnly;
    }

    public thousandFormatter(x) {
        return Math.ceil(x).toLocaleString('en-US');
    }

    public get expensiveHook() {
        const {
            currentYear,
            selectedDateRange,
            selectedAsOfDate,
            selectedAgents,
            selectedCustomers,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
            selectedColumn,
            selectedTimeframe,
        } = this;
        return JSON.stringify([
            currentYear,
            selectedDateRange,
            selectedAsOfDate,
            selectedAgents,
            selectedCustomers,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
            selectedColumn,
            selectedTimeframe,
        ]);
    }

    public async expensiveCalc() {
        const jobId = (this.currentJobId = uuid.v4());
        this.$forceUpdate();
        this.generateLoadingText(0.2, 0.35);

        const col = this.selectedColumn;
        const currentFetchKey = this._dataFetchKey;
        const useCache = this._cachedData !== null && this._cachedDataKey === currentFetchKey;

        // ========================================
        // FAST: Handle "newCustomer" column - SQL COUNT only
        // ========================================
        if (col === 'newCustomer') {
            // Check cache for newCustomer counts
            if (useCache && this._cachedData!.newCustomerCounts.length > 0) {
                this.columnDatas = [...this._cachedData!.newCustomerCounts];
                this.comparedColumnDatas = [...this._cachedData!.comparedNewCustomerCounts];
            } else {
                this.generateLoadingText(0.35, 0.6);
                const queryHead = await this._ensureTablesLoaded(['globalCustomers'] as any[]);
                this.checkInterrupted(jobId);
                this.generateLoadingText(0.6, 0.95);

                const globalTable = getTableName('globalCustomers');
                const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
                let agentFilter = '';
                if (this.selectedAgents.length > 0) {
                    agentFilter = ` AND "FIRSTDEALAGENT" IN (${this.selectedAgents.map((a) => `'${escapeStr(a)}'`).join(',')})`;
                }
                let customerFilter = '';
                if (this.selectedCustomers.length > 0) {
                    customerFilter = ` AND ${COL.CODE} IN (${this.selectedCustomers.map((c) => `'${escapeStr(c)}'`).join(',')})`;
                }

                const buildCountQuery = (range: [number, number]) => `
                    SELECT
                        ${sqlExtractYear('"FIRSTDEAL"')},
                        ${sqlExtractMonth('"FIRSTDEAL"')},
                        COUNT(*) as count
                    FROM ${globalTable}
                    WHERE TRY_CAST("FIRSTDEAL" AS BIGINT) >= ${range[0]}
                      AND TRY_CAST("FIRSTDEAL" AS BIGINT) <= ${range[1]}
                      ${agentFilter}${customerFilter}
                    GROUP BY year, month
                    ORDER BY year, month
                `;

                const [countsByMonth, comparedCountsByMonth] = await Promise.all([
                    queryHead.rawQuery(buildCountQuery(this.computedYearRange)),
                    queryHead.rawQuery(buildCountQuery(this.compareYearRange)),
                ]);
                this.checkInterrupted(jobId);
                this.generateLoadingText(0.95);

                const parseResult = (rows: any[]) => rows.map((r) => ({
                    year: Number(r.year), month: Number(r.month), count: Number(r.count) || 0,
                }));

                this.columnDatas = this._alignToMonthArray(
                    parseResult(countsByMonth), this.computedYearRange, (i) => i.count);
                this.comparedColumnDatas = this._alignToMonthArray(
                    parseResult(comparedCountsByMonth), this.compareYearRange, (i) => i.count);

                // Store in cache if we have one
                if (this._cachedData && this._cachedDataKey === currentFetchKey) {
                    this._cachedData.newCustomerCounts = [...this.columnDatas];
                    this._cachedData.comparedNewCustomerCounts = [...this.comparedColumnDatas];
                }
            }

            this.generateLoadingText(1);
            this.xAxisData = this.buildMonthLabels();

            if (this.selectedTimeframe !== 'Monthly') {
                const monthLabels = this.xAxisData.map((t) => ({ text: t }));
                this.applyTimeframeGrouping(monthLabels);
            }

            this.series = [
                { name: this.columnName + ' This Year', type: 'bar', barWidth: '30%',
                  itemStyle: {color: GobiColor.COLOR1}, data: this.columnDatas },
                { name: this.columnName + ' Last Year', type: 'bar', barWidth: '30%',
                  itemStyle: {color: GobiColor.COLOR3}, data: this.comparedColumnDatas },
            ];
            this.yearEndTotal = gdbx.currencySymbol + ' '
                + this.thousandFormatter(this.columnDatas.reduce((a, b) => a + b, 0));
            this.comparedYearEndTotal = gdbx.currencySymbol + ' '
                + this.thousandFormatter(this.comparedColumnDatas.reduce((a, b) => a + b, 0));
            this.saveHistory('series', 'xAxisData', 'yearEndTotal', 'comparedYearEndTotal');
            return;
        }

        // ========================================
        // Check if we can use cached data (filters unchanged)
        // ========================================
        let slIvCsAmounts: number[];
        let slDnAmounts: number[];
        let slCnAmounts: number[];
        let slIvCsProfits: number[];
        let slDnProfits: number[];
        let slCnProfits: number[];
        let arIvAmounts: number[];
        let arCnAmounts: number[];
        let arDnAmounts: number[];
        let slIvCsAmountsLY: number[];
        let slDnAmountsLY: number[];
        let slCnAmountsLY: number[];
        let slIvCsProfitsLY: number[];
        let slDnProfitsLY: number[];
        let slCnProfitsLY: number[];
        let arIvAmountsLY: number[];
        let arCnAmountsLY: number[];
        let arDnAmountsLY: number[];
        let slNewIvCsAmounts: number[];
        let slNewDnAmounts: number[];
        let slNewCnAmounts: number[];
        let slNewIvCsProfits: number[];
        let slNewDnProfits: number[];
        let slNewCnProfits: number[];
        let arNewIvAmounts: number[];
        let arNewCnAmounts: number[];
        let arNewDnAmounts: number[];
        let slNewIvCsAmountsLY: number[];
        let slNewDnAmountsLY: number[];
        let slNewCnAmountsLY: number[];
        let slNewIvCsProfitsLY: number[];
        let slNewDnProfitsLY: number[];
        let slNewCnProfitsLY: number[];
        let arNewIvAmountsLY: number[];
        let arNewCnAmountsLY: number[];
        let arNewDnAmountsLY: number[];

        if (useCache) {
            // ========================================
            // USE CACHED DATA - instant switch between columns/timeframes
            // ========================================
            slIvCsAmounts = this._cachedData!.slIvCsAmounts;
            slDnAmounts = this._cachedData!.slDnAmounts;
            slCnAmounts = this._cachedData!.slCnAmounts;
            slIvCsProfits = this._cachedData!.slIvCsProfits;
            slDnProfits = this._cachedData!.slDnProfits;
            slCnProfits = this._cachedData!.slCnProfits;
            arIvAmounts = this._cachedData!.arIvAmounts;
            arCnAmounts = this._cachedData!.arCnAmounts;
            arDnAmounts = this._cachedData!.arDnAmounts;
            slIvCsAmountsLY = this._cachedData!.slIvCsAmountsLY;
            slDnAmountsLY = this._cachedData!.slDnAmountsLY;
            slCnAmountsLY = this._cachedData!.slCnAmountsLY;
            slIvCsProfitsLY = this._cachedData!.slIvCsProfitsLY;
            slDnProfitsLY = this._cachedData!.slDnProfitsLY;
            slCnProfitsLY = this._cachedData!.slCnProfitsLY;
            arIvAmountsLY = this._cachedData!.arIvAmountsLY;
            arCnAmountsLY = this._cachedData!.arCnAmountsLY;
            arDnAmountsLY = this._cachedData!.arDnAmountsLY;
            slNewIvCsAmounts = this._cachedData!.slNewIvCsAmounts;
            slNewDnAmounts = this._cachedData!.slNewDnAmounts;
            slNewCnAmounts = this._cachedData!.slNewCnAmounts;
            slNewIvCsProfits = this._cachedData!.slNewIvCsProfits;
            slNewDnProfits = this._cachedData!.slNewDnProfits;
            slNewCnProfits = this._cachedData!.slNewCnProfits;
            arNewIvAmounts = this._cachedData!.arNewIvAmounts;
            arNewCnAmounts = this._cachedData!.arNewCnAmounts;
            arNewDnAmounts = this._cachedData!.arNewDnAmounts;
            slNewIvCsAmountsLY = this._cachedData!.slNewIvCsAmountsLY;
            slNewDnAmountsLY = this._cachedData!.slNewDnAmountsLY;
            slNewCnAmountsLY = this._cachedData!.slNewCnAmountsLY;
            slNewIvCsProfitsLY = this._cachedData!.slNewIvCsProfitsLY;
            slNewDnProfitsLY = this._cachedData!.slNewDnProfitsLY;
            slNewCnProfitsLY = this._cachedData!.slNewCnProfitsLY;
            arNewIvAmountsLY = this._cachedData!.arNewIvAmountsLY;
            arNewCnAmountsLY = this._cachedData!.arNewCnAmountsLY;
            arNewDnAmountsLY = this._cachedData!.arNewDnAmountsLY;
        } else {
            // ========================================
            // FETCH NEW DATA - filters changed
            // ========================================

            this.generateLoadingText(0.35, 0.9);

            const queryHead = await this._ensureTablesLoaded(
                ['sales', 'creditNotes', 'customerTrades', 'globalCustomers'] as any[],
            );
            this.checkInterrupted(jobId);

            const salesTable = getTableName('sales');
            const cnTable = getTableName('creditNotes');
            const arTable = getTableName('customerTrades');

            const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
            let agentFilter = '';
            if (this.selectedAgents.length > 0) {
                agentFilter = ` AND ${COL.AGENT} IN (${this.selectedAgents.map((a) => `'${escapeStr(a)}'`).join(',')})`;
            }
            let customerFilter = '';
            if (this.selectedCustomers.length > 0) {
                customerFilter = ` AND ${COL.CODE} IN (${this.selectedCustomers.map((c) => `'${escapeStr(c)}'`).join(',')})`;
            }

            // Load all data in PARALLEL
            const qh = queryHead;
            const af = agentFilter;
            const cf = customerFilter;
            const yr = this.computedYearRange;
            const ly = this.compareYearRange;

            const [
                salesDataThisYear,
                creditNotesThisYear,
                customerTradesThisYear,
                salesDataLastYear,
                creditNotesLastYear,
                customerTradesLastYear,
                salesDataNewThisYear,
                creditNotesNewThisYear,
                customerTradesNewThisYear,
                salesDataNewLastYear,
                creditNotesNewLastYear,
                customerTradesNewLastYear,
            ] = await Promise.all([
                this._loadSalesDataByMonth(qh, salesTable, yr, af, cf),
                this._loadCreditNotesDataByMonth(qh, cnTable, yr, af, cf),
                this._loadCustomerTradesDataByMonth(qh, arTable, yr, af, cf),
                this._loadSalesDataByMonth(qh, salesTable, ly, af, cf),
                this._loadCreditNotesDataByMonth(qh, cnTable, ly, af, cf),
                this._loadCustomerTradesDataByMonth(qh, arTable, ly, af, cf),
                // New customers this year
                this._loadSalesDataByMonth(qh, salesTable, yr, af, cf, yr),
                this._loadCreditNotesDataByMonth(qh, cnTable, yr, af, cf, yr),
                this._loadCustomerTradesDataByMonth(qh, arTable, yr, af, cf, yr),
                // New customers last year
                this._loadSalesDataByMonth(qh, salesTable, ly, af, cf, ly),
                this._loadCreditNotesDataByMonth(qh, cnTable, ly, af, cf, ly),
                this._loadCustomerTradesDataByMonth(qh, arTable, ly, af, cf, ly),
            ]);
            this.checkInterrupted(jobId);

            this.generateLoadingText(0.95);

            // ========================================
            // Convert raw data to 12-element arrays
            // ========================================
            const align = <T>(
                data: Array<{ year: number; month: number } & T>,
                range: [number, number],
                fn: (i: T) => number,
            ) => this._alignToMonthArray(data, range, fn);

            slIvCsAmounts = align(salesDataThisYear, this.computedYearRange, (i) => i.ivCsAmount);
            slDnAmounts = align(salesDataThisYear, this.computedYearRange, (i) => i.dnAmount);
            slCnAmounts = align(creditNotesThisYear, this.computedYearRange, (i) => i.amount);
            slIvCsProfits = align(salesDataThisYear, this.computedYearRange, (i) => i.ivCsProfit);
            slDnProfits = align(salesDataThisYear, this.computedYearRange, (i) => i.dnProfit);
            slCnProfits = align(creditNotesThisYear, this.computedYearRange, (i) => i.profit);

            arIvAmounts = align(customerTradesThisYear, this.computedYearRange, (i) => i.ivAmount);
            arCnAmounts = align(customerTradesThisYear, this.computedYearRange, (i) => i.cnAmount);
            arDnAmounts = align(customerTradesThisYear, this.computedYearRange, (i) => i.dnAmount);

            slIvCsAmountsLY = align(salesDataLastYear, this.compareYearRange, (i) => i.ivCsAmount);
            slDnAmountsLY = align(salesDataLastYear, this.compareYearRange, (i) => i.dnAmount);
            slCnAmountsLY = align(creditNotesLastYear, this.compareYearRange, (i) => i.amount);
            slIvCsProfitsLY = align(salesDataLastYear, this.compareYearRange, (i) => i.ivCsProfit);
            slDnProfitsLY = align(salesDataLastYear, this.compareYearRange, (i) => i.dnProfit);
            slCnProfitsLY = align(creditNotesLastYear, this.compareYearRange, (i) => i.profit);

            arIvAmountsLY = align(customerTradesLastYear, this.compareYearRange, (i) => i.ivAmount);
            arCnAmountsLY = align(customerTradesLastYear, this.compareYearRange, (i) => i.cnAmount);
            arDnAmountsLY = align(customerTradesLastYear, this.compareYearRange, (i) => i.dnAmount);

            slNewIvCsAmounts = align(salesDataNewThisYear, this.computedYearRange, (i) => i.ivCsAmount);
            slNewDnAmounts = align(salesDataNewThisYear, this.computedYearRange, (i) => i.dnAmount);
            slNewCnAmounts = align(creditNotesNewThisYear, this.computedYearRange, (i) => i.amount);
            slNewIvCsProfits = align(salesDataNewThisYear, this.computedYearRange, (i) => i.ivCsProfit);
            slNewDnProfits = align(salesDataNewThisYear, this.computedYearRange, (i) => i.dnProfit);
            slNewCnProfits = align(creditNotesNewThisYear, this.computedYearRange, (i) => i.profit);

            arNewIvAmounts = align(customerTradesNewThisYear, this.computedYearRange, (i) => i.ivAmount);
            arNewCnAmounts = align(customerTradesNewThisYear, this.computedYearRange, (i) => i.cnAmount);
            arNewDnAmounts = align(customerTradesNewThisYear, this.computedYearRange, (i) => i.dnAmount);

            slNewIvCsAmountsLY = align(salesDataNewLastYear, this.compareYearRange, (i) => i.ivCsAmount);
            slNewDnAmountsLY = align(salesDataNewLastYear, this.compareYearRange, (i) => i.dnAmount);
            slNewCnAmountsLY = align(creditNotesNewLastYear, this.compareYearRange, (i) => i.amount);
            slNewIvCsProfitsLY = align(salesDataNewLastYear, this.compareYearRange, (i) => i.ivCsProfit);
            slNewDnProfitsLY = align(salesDataNewLastYear, this.compareYearRange, (i) => i.dnProfit);
            slNewCnProfitsLY = align(creditNotesNewLastYear, this.compareYearRange, (i) => i.profit);

            arNewIvAmountsLY = align(customerTradesNewLastYear, this.compareYearRange, (i) => i.ivAmount);
            arNewCnAmountsLY = align(customerTradesNewLastYear, this.compareYearRange, (i) => i.cnAmount);
            arNewDnAmountsLY = align(customerTradesNewLastYear, this.compareYearRange, (i) => i.dnAmount);

            // ========================================
            // Store in cache for instant column/timeframe switching
            // ========================================
            this._cachedData = {
                slIvCsAmounts,
                slDnAmounts,
                slCnAmounts,
                slIvCsProfits,
                slDnProfits,
                slCnProfits,
                arIvAmounts,
                arCnAmounts,
                arDnAmounts,
                slIvCsAmountsLY,
                slDnAmountsLY,
                slCnAmountsLY,
                slIvCsProfitsLY,
                slDnProfitsLY,
                slCnProfitsLY,
                arIvAmountsLY,
                arCnAmountsLY,
                arDnAmountsLY,
                slNewIvCsAmounts,
                slNewDnAmounts,
                slNewCnAmounts,
                slNewIvCsProfits,
                slNewDnProfits,
                slNewCnProfits,
                arNewIvAmounts,
                arNewCnAmounts,
                arNewDnAmounts,
                slNewIvCsAmountsLY,
                slNewDnAmountsLY,
                slNewCnAmountsLY,
                slNewIvCsProfitsLY,
                slNewDnProfitsLY,
                slNewCnProfitsLY,
                arNewIvAmountsLY,
                arNewCnAmountsLY,
                arNewDnAmountsLY,
                newCustomerCounts: [],
                comparedNewCustomerCounts: [],
            };
            this._cachedDataKey = currentFetchKey;
        }

        // ========================================
        // Calculate amounts based on toggle settings
        // (always runs - toggles can change without refetch)
        // ========================================
        const calcSL = (iv: number[], cn: number[], dn: number[]) =>
            iv.map((v, i) => v - (this.includeCreditNote ? cn[i] : 0) + (this.includeDebitNote ? dn[i] : 0));

        const calcAR = (iv: number[], cn: number[], dn: number[]) =>
            iv.map((v, i) => v - (this.includeCreditNote ? cn[i] : 0) + (this.includeDebitNote ? dn[i] : 0));

        // SL Amounts
        const slAllAmounts = calcSL(slIvCsAmounts, slCnAmounts, slDnAmounts);
        const slNewAmounts = calcSL(slNewIvCsAmounts, slNewCnAmounts, slNewDnAmounts);
        const slAllAmountsLY = calcSL(slIvCsAmountsLY, slCnAmountsLY, slDnAmountsLY);
        const slNewAmountsLY = calcSL(slNewIvCsAmountsLY, slNewCnAmountsLY, slNewDnAmountsLY);

        // AR Amounts
        const arAllAmounts = calcAR(arIvAmounts, arCnAmounts, arDnAmounts);
        const arNewAmounts = calcAR(arNewIvAmounts, arNewCnAmounts, arNewDnAmounts);
        const arAllAmountsLY = calcAR(arIvAmountsLY, arCnAmountsLY, arDnAmountsLY);
        const arNewAmountsLY = calcAR(arNewIvAmountsLY, arNewCnAmountsLY, arNewDnAmountsLY);

        // SL Profits
        const slAllProfits = calcSL(slIvCsProfits, slCnProfits, slDnProfits);
        const slNewProfits = calcSL(slNewIvCsProfits, slNewCnProfits, slNewDnProfits);
        const slAllProfitsLY = calcSL(slIvCsProfitsLY, slCnProfitsLY, slDnProfitsLY);
        const slNewProfitsLY = calcSL(slNewIvCsProfitsLY, slNewCnProfitsLY, slNewDnProfitsLY);

        // AR Profits = SL Profits + (AR Amount - SL Amount)
        const arAllProfits = slAllProfits.map((p, i) => p + (arAllAmounts[i] - slAllAmounts[i]));
        const arNewProfits = slNewProfits.map((p, i) => p + (arNewAmounts[i] - slNewAmounts[i]));
        const arAllProfitsLY = slAllProfitsLY.map((p, i) => p + (arAllAmountsLY[i] - slAllAmountsLY[i]));
        const arNewProfitsLY = slNewProfitsLY.map((p, i) => p + (arNewAmountsLY[i] - slNewAmountsLY[i]));

        // Select based on SL/AR toggle
        const allAmounts = this.slPhModuleOnly ? slAllAmounts : arAllAmounts;
        const newAmounts = this.slPhModuleOnly ? slNewAmounts : arNewAmounts;
        const allAmountsLY = this.slPhModuleOnly ? slAllAmountsLY : arAllAmountsLY;
        const newAmountsLY = this.slPhModuleOnly ? slNewAmountsLY : arNewAmountsLY;

        const allProfits = this.slPhModuleOnly ? slAllProfits : arAllProfits;
        const newProfits = this.slPhModuleOnly ? slNewProfits : arNewProfits;
        const allProfitsLY = this.slPhModuleOnly ? slAllProfitsLY : arAllProfitsLY;
        const newProfitsLY = this.slPhModuleOnly ? slNewProfitsLY : arNewProfitsLY;

        // CN amounts for "Credit Note Amt" column
        const cnAmounts = this.slPhModuleOnly ? slCnAmounts : arCnAmounts;
        const cnAmountsLY = this.slPhModuleOnly ? slCnAmountsLY : arCnAmountsLY;

        // ========================================
        // Set data based on selected column
        // ========================================
        switch (col) {
            case 'allSalesAmount':
                this.columnDatas = allAmounts;
                this.comparedColumnDatas = allAmountsLY;
                break;
            case 'allCreditNote':
                this.columnDatas = cnAmounts;
                this.comparedColumnDatas = cnAmountsLY;
                break;
            case 'newSalesAmount':
                this.columnDatas = newAmounts;
                this.comparedColumnDatas = newAmountsLY;
                break;
            case 'existingSalesAmount':
                this.columnDatas = allAmounts.map((v, i) => v - newAmounts[i]);
                this.comparedColumnDatas = allAmountsLY.map((v, i) => v - newAmountsLY[i]);
                break;
            case 'allProfitAmount':
                this.columnDatas = allProfits;
                this.comparedColumnDatas = allProfitsLY;
                break;
            case 'newProfitAmount':
                this.columnDatas = newProfits;
                this.comparedColumnDatas = newProfitsLY;
                break;
            case 'existingProfitAmount':
                this.columnDatas = allProfits.map((v, i) => v - newProfits[i]);
                this.comparedColumnDatas = allProfitsLY.map((v, i) => v - newProfitsLY[i]);
                break;
        }

        this.generateLoadingText(1);

        // Build x-axis labels
        this.xAxisData = this.buildMonthLabels();

        // Apply timeframe grouping if needed
        if (this.selectedTimeframe !== 'Monthly') {
            const monthLabels = this.xAxisData.map((t) => ({ text: t }));
            this.applyTimeframeGrouping(monthLabels);
        }

        this.series = [
            {
                name: this.columnName + ' This Year',
                type: 'bar',
                barWidth: '30%',
                itemStyle: { color: GobiColor.COLOR1 },
                data: this.columnDatas,
            },
            {
                name: this.columnName + ' Last Year',
                type: 'bar',
                barWidth: '30%',
                itemStyle: { color: GobiColor.COLOR3 },
                data: this.comparedColumnDatas,
            },
        ];

        this.yearEndTotal = gdbx.currencySymbol + ' '
            + this.thousandFormatter(this.columnDatas.reduce((a, b) => a + b, 0));
        this.comparedYearEndTotal = gdbx.currencySymbol + ' '
            + this.thousandFormatter(this.comparedColumnDatas.reduce((a, b) => a + b, 0));

        this.saveHistory('series', 'xAxisData', 'yearEndTotal', 'comparedYearEndTotal');
    }

    public onHovering(value: boolean, filterId: string) {
        this.$emit('hovering', value, filterId);
    }

    public onEditing(value: boolean, filterId: string) {
        this.$emit('editing', value, filterId);
    }

    /**
     * Build month labels for x-axis (oldest-first, 12 months)
     */
    public buildMonthLabels(): string[] {
        const labels: string[] = [];
        const start = moment(this.computedYearRange[0]);
        for (let i = 0; i < 12; i++) {
            labels.push(start.clone().add(i, 'month').format('MMM YY'));
        }
        return labels;
    }

    private async _loadSalesDataByMonth(
        queryHead: any, tableName: string, dateRange: [number, number],
        agentFilter: string, customerFilter: string,
        newCustomerDateRange?: [number, number],
    ): Promise<Array<{
        year: number; month: number;
        ivCsAmount: number; dnAmount: number;
        ivCsProfit: number; dnProfit: number;
    }>> {
        const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${dateRange[0]} AND ${dateRange[1]}`;
        const filterClause = newCustomerDateRange
            ? buildNewCustomerFilter({
                dateRange: newCustomerDateRange,
                agents: this.selectedAgents,
                customers: this.selectedCustomers,
                useFirstDealAgent: true,
            })
            : customerFilter;

        const results = await queryHead.rawQuery(`
            SELECT
                ${sqlExtractYear(COL.DOCDATE)},
                ${sqlExtractMonth(COL.DOCDATE)},
                ${sqlSumByDocType(['IV', 'CS'], COL.AMOUNT, 'iv_cs_amount')},
                ${sqlSumByDocType(['DN'], COL.AMOUNT, 'dn_amount')},
                ${sqlSumByDocType(['IV', 'CS'], COL.PROFIT, 'iv_cs_profit')},
                ${sqlSumByDocType(['DN'], COL.PROFIT, 'dn_profit')}
            FROM ${tableName}
            WHERE ${dateFilter}${agentFilter}${filterClause}
            GROUP BY year, month
            ORDER BY year, month
        `);

        return results.map((r) => ({
            year: Number(r.year),
            month: Number(r.month),
            ivCsAmount: Number(r.iv_cs_amount) || 0,
            dnAmount: Number(r.dn_amount) || 0,
            ivCsProfit: Number(r.iv_cs_profit) || 0,
            dnProfit: Number(r.dn_profit) || 0,
        }));
    }

    private async _loadCreditNotesDataByMonth(
        queryHead: any, tableName: string, dateRange: [number, number],
        agentFilter: string, customerFilter: string,
        newCustomerDateRange?: [number, number],
    ): Promise<Array<{ year: number; month: number; amount: number; profit: number }>> {
        const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${dateRange[0]} AND ${dateRange[1]}`;
        const filterClause = newCustomerDateRange
            ? buildNewCustomerFilter({
                dateRange: newCustomerDateRange,
                agents: this.selectedAgents,
                customers: this.selectedCustomers,
                useFirstDealAgent: true,
            })
            : customerFilter;

        const results = await queryHead.rawQuery(`
            SELECT
                ${sqlExtractYear(COL.DOCDATE)},
                ${sqlExtractMonth(COL.DOCDATE)},
                ${sqlSum(COL.AMOUNT, 'amount')},
                ${sqlSum(COL.PROFIT, 'profit')}
            FROM ${tableName}
            WHERE ${dateFilter}${agentFilter}${filterClause}
            GROUP BY year, month
            ORDER BY year, month
        `);

        return results.map((r) => ({
            year: Number(r.year),
            month: Number(r.month),
            amount: Number(r.amount) || 0,
            profit: Number(r.profit) || 0,
        }));
    }

    private async _loadCustomerTradesDataByMonth(
        queryHead: any, tableName: string, dateRange: [number, number],
        agentFilter: string, customerFilter: string,
        newCustomerDateRange?: [number, number],
    ): Promise<Array<{
        year: number; month: number;
        ivAmount: number; cnAmount: number; dnAmount: number;
    }>> {
        const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${dateRange[0]} AND ${dateRange[1]}`;
        const filterClause = newCustomerDateRange
            ? buildNewCustomerFilter({
                dateRange: newCustomerDateRange,
                agents: this.selectedAgents,
                customers: this.selectedCustomers,
                useFirstDealAgent: true,
            })
            : customerFilter;

        const results = await queryHead.rawQuery(`
            SELECT
                ${sqlExtractYear(COL.DOCDATE)},
                ${sqlExtractMonth(COL.DOCDATE)},
                ${sqlSumByDocType(['IV'], COL.LOCALAMOUNT, 'iv_amount')},
                ${sqlSumByDocType(['CN'], COL.LOCALAMOUNT, 'cn_amount')},
                ${sqlSumByDocType(['DN'], COL.LOCALAMOUNT, 'dn_amount')}
            FROM ${tableName}
            WHERE ${dateFilter}${agentFilter}${filterClause}
            GROUP BY year, month
            ORDER BY year, month
        `);

        return results.map((r) => ({
            year: Number(r.year),
            month: Number(r.month),
            ivAmount: Number(r.iv_amount) || 0,
            cnAmount: Number(r.cn_amount) || 0,
            dnAmount: Number(r.dn_amount) || 0,
        }));
    }

    /**
     * Convert raw month data to 12-element array aligned to dateRange
     */
    private _alignToMonthArray<T>(
        data: Array<{ year: number; month?: number } & T>,
        dateRange: [number, number],
        getValue: (item: T) => number,
    ): number[] {
        const result = Array(12).fill(0);
        const startMonth = moment(dateRange[0]).month() + 1; // 1-based
        const startYear = moment(dateRange[0]).year();

        for (const item of data) {
            const month = item.month || 1;
            const pos = (item.year - startYear) * 12 + (month - startMonth);
            if (pos >= 0 && pos < 12) {
                result[pos] = getValue(item as any);
            }
        }
        return result;
    }
}
